/**
 * Client-side Script File Parser — for Vault/BYOK users.
 *
 * All parsing happens in the browser. Script content NEVER leaves the client.
 *
 * Supported: PDF, DOCX, FDX (Final Draft), Fountain, TXT, RTF,
 *            Celtx, OSF, Fade In, Highland
 */

// ── Format Detection ───────────────────────────────────────

const EXT_MAP = {
  '.pdf': 'pdf',
  '.docx': 'docx',
  '.doc': 'docx',
  '.fdx': 'fdx',
  '.fountain': 'fountain',
  '.spmd': 'fountain',
  '.highland': 'highland',
  '.txt': 'txt',
  '.text': 'txt',
  '.rtf': 'rtf',
  '.celtx': 'celtx',
  '.osf': 'osf',
  '.fadein': 'fadein',
};

export const SUPPORTED_EXTENSIONS = Object.keys(EXT_MAP);

export const ACCEPTED_TYPES = SUPPORTED_EXTENSIONS.map((e) => e).join(',');

function detectFormat(filename) {
  const ext = '.' + filename.split('.').pop().toLowerCase();
  return EXT_MAP[ext] || 'txt';
}

// ── Main Entry Point ───────────────────────────────────────

/**
 * Parse a script file entirely client-side.
 * @param {File} file - The file object from input/drop
 * @returns {Promise<{text: string, format: string, pageCount: number|null, metadata: object}>}
 */
export async function parseScriptFile(file) {
  const fmt = detectFormat(file.name);
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  const parsers = {
    pdf: parsePDF,
    docx: parseDOCX,
    fdx: parseFDX,
    fountain: parseFountain,
    txt: parseTXT,
    rtf: parseRTF,
    celtx: parseCeltx,
    osf: parseOSF,
    fadein: parseFadeIn,
    highland: parseHighland,
  };

  const parser = parsers[fmt] || parseTXT;

  try {
    const result = await parser(bytes, buffer);
    result.format = fmt;
    result.text = cleanText(result.text || '');
    return result;
  } catch (err) {
    console.error(`Failed to parse ${file.name} as ${fmt}:`, err);
    throw new Error(`Could not parse ${file.name}. ${err.message || 'Try a different format.'}`);
  }
}

function cleanText(text) {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\x00/g, '')
    .replace(/\n{4,}/g, '\n\n\n')
    .trim();
}

// ── PDF Parser (uses pdf.js from CDN) ──────────────────────

let pdfjsLib = null;

async function loadPdfJs() {
  if (pdfjsLib) return pdfjsLib;

  // Dynamically load pdf.js from CDN
  if (!window.pdfjsLib) {
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load PDF.js'));
      document.head.appendChild(script);
    });
  }

  pdfjsLib = window.pdfjsLib;
  pdfjsLib.GlobalWorkerOptions.workerSrc =
    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  return pdfjsLib;
}

async function parsePDF(bytes, buffer) {
  const lib = await loadPdfJs();
  const doc = await lib.getDocument({ data: buffer }).promise;
  const pages = [];

  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const strings = content.items.map((item) => item.str);
    pages.push(strings.join(' '));
  }

  return {
    text: pages.join('\n\n'),
    pageCount: doc.numPages,
    metadata: {},
  };
}

// ── DOCX Parser (uses JSZip for ZIP, then XML parsing) ─────

async function parseDOCX(bytes, buffer) {
  const zip = await loadAndUnzip(buffer);
  const docXml = zip['word/document.xml'];
  if (!docXml) throw new Error('Invalid DOCX: no document.xml found');

  const parser = new DOMParser();
  const doc = parser.parseFromString(docXml, 'application/xml');
  const ns = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
  const paragraphs = doc.getElementsByTagNameNS(ns, 'p');
  const lines = [];

  for (let i = 0; i < paragraphs.length; i++) {
    const texts = paragraphs[i].getElementsByTagNameNS(ns, 't');
    let line = '';
    for (let j = 0; j < texts.length; j++) {
      line += texts[j].textContent || '';
    }
    lines.push(line);
  }

  // Extract metadata from docProps/core.xml
  const metadata = {};
  if (zip['docProps/core.xml']) {
    try {
      const coreDoc = parser.parseFromString(zip['docProps/core.xml'], 'application/xml');
      const title = coreDoc.getElementsByTagName('dc:title')[0];
      const author = coreDoc.getElementsByTagName('dc:creator')[0];
      if (title?.textContent) metadata.title = title.textContent;
      if (author?.textContent) metadata.author = author.textContent;
    } catch (e) { /* ignore */ }
  }

  return { text: lines.join('\n'), pageCount: null, metadata };
}

// ── Final Draft (.fdx) Parser ──────────────────────────────

async function parseFDX(bytes) {
  const text = new TextDecoder('utf-8').decode(bytes);
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'application/xml');

  const lines = [];

  const paragraphs = doc.getElementsByTagName('Paragraph');
  for (let i = 0; i < paragraphs.length; i++) {
    const para = paragraphs[i];
    const type = para.getAttribute('Type') || 'Action';
    const textNodes = para.getElementsByTagName('Text');
    let content = '';
    for (let j = 0; j < textNodes.length; j++) {
      content += textNodes[j].textContent || '';
    }
    content = content.trim();
    if (!content) { lines.push(''); continue; }

    switch (type) {
      case 'Scene Heading':
        lines.push('', content.toUpperCase(), '');
        break;
      case 'Character':
        lines.push('', '          ' + content.toUpperCase());
        break;
      case 'Parenthetical':
        lines.push('    (' + content + ')');
        break;
      case 'Dialogue':
        lines.push('    ' + content);
        break;
      case 'Transition':
        lines.push('', '                                        ' + content.toUpperCase(), '');
        break;
      default:
        lines.push(content);
    }
  }

  return { text: lines.join('\n'), pageCount: null, metadata: {} };
}

// ── Fountain Parser ────────────────────────────────────────

async function parseFountain(bytes) {
  const text = new TextDecoder('utf-8').decode(bytes);
  const allLines = text.split('\n');
  const metadata = {};
  let bodyStart = 0;

  // Parse title page
  if (allLines[0]?.includes(':')) {
    for (let i = 0; i < allLines.length; i++) {
      if (allLines[i].trim() === '') { bodyStart = i + 1; break; }
      if (allLines[i].includes(':')) {
        const [key, ...val] = allLines[i].split(':');
        metadata[key.trim().toLowerCase()] = val.join(':').trim();
      }
    }
  }

  const body = allLines.slice(bodyStart);
  const output = [];

  for (const line of body) {
    const s = line.trim();
    if (s.startsWith('.') && !s.startsWith('..')) {
      output.push('', s.slice(1).toUpperCase(), '');
    } else if (s.startsWith('>') && !s.endsWith('<')) {
      output.push('                                        ' + s.slice(1).trim().toUpperCase());
    } else if (s.startsWith('>') && s.endsWith('<')) {
      output.push('    ' + s.slice(1, -1).trim());
    } else if (s.startsWith('[[') && s.endsWith(']]')) {
      continue;
    } else if (s.startsWith('#')) {
      output.push('', s.replace(/^#+\s*/, '').toUpperCase(), '');
    } else if (s.startsWith('=') && !s.startsWith('==')) {
      continue;
    } else if (s === '===') {
      output.push('');
    } else {
      output.push(line);
    }
  }

  let result = output.join('\n');
  // Remove boneyard
  result = result.replace(/\/\*[\s\S]*?\*\//g, '');
  // Remove emphasis markers
  result = result.replace(/\*{3}(.+?)\*{3}/g, '$1');
  result = result.replace(/\*{2}(.+?)\*{2}/g, '$1');
  result = result.replace(/\*(.+?)\*/g, '$1');
  result = result.replace(/_(.+?)_/g, '$1');

  return { text: result, pageCount: null, metadata };
}

// ── TXT Parser ─────────────────────────────────────────────

async function parseTXT(bytes) {
  const text = new TextDecoder('utf-8').decode(bytes);
  return { text, pageCount: null, metadata: {} };
}

// ── RTF Parser ─────────────────────────────────────────────

async function parseRTF(bytes) {
  const text = new TextDecoder('utf-8').decode(bytes);
  // Simple RTF stripping — handles most screenplay RTF files
  let result = text;
  // Remove RTF header
  result = result.replace(/^\{\\rtf[\s\S]*?(?=\\pard|$)/, '');
  // Remove control words but keep text
  result = result.replace(/\\par\b/g, '\n');
  result = result.replace(/\\tab\b/g, '\t');
  result = result.replace(/\\line\b/g, '\n');
  result = result.replace(/\\\{/g, '{');
  result = result.replace(/\\\}/g, '}');
  result = result.replace(/\\\\/g, '\\');
  // Remove remaining control words and groups
  result = result.replace(/\{[^{}]*\}/g, '');
  result = result.replace(/\\[a-z]+\d*\s?/gi, '');
  result = result.replace(/[{}]/g, '');
  // Remove unicode escapes
  result = result.replace(/\\u\d+\s?/g, '');
  result = result.replace(/\\'[0-9a-f]{2}/gi, '');

  return { text: result, pageCount: null, metadata: {} };
}

// ── Celtx Parser ───────────────────────────────────────────

async function parseCeltx(bytes, buffer) {
  try {
    const zip = await loadAndUnzip(buffer);
    // Find script HTML
    const scriptKey = Object.keys(zip).find(
      (k) => k.startsWith('script') && k.endsWith('.html')
    ) || Object.keys(zip).find((k) => k.endsWith('.html'));

    if (!scriptKey) throw new Error('No script found in Celtx archive');
    return parseScriptHTML(zip[scriptKey]);
  } catch (e) {
    // Might be raw XML/HTML
    const text = new TextDecoder('utf-8').decode(bytes);
    return parseScriptHTML(text);
  }
}

function parseScriptHTML(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const paragraphs = doc.querySelectorAll('p');
  const lines = [];

  paragraphs.forEach((p) => {
    const cls = (p.className || '').toLowerCase();
    const text = p.textContent.trim();
    if (!text) { lines.push(''); return; }

    if (cls.includes('sceneheading') || cls.includes('scene-heading')) {
      lines.push('', text.toUpperCase(), '');
    } else if (cls.includes('character')) {
      lines.push('', '          ' + text.toUpperCase());
    } else if (cls.includes('parenthetical')) {
      lines.push('    (' + text + ')');
    } else if (cls.includes('dialogue')) {
      lines.push('    ' + text);
    } else if (cls.includes('transition')) {
      lines.push('', '                                        ' + text.toUpperCase(), '');
    } else {
      lines.push(text);
    }
  });

  // Fallback
  if (lines.length === 0) {
    return { text: doc.body?.textContent || html.replace(/<[^>]+>/g, '\n'), pageCount: null, metadata: {} };
  }

  return { text: lines.join('\n'), pageCount: null, metadata: {} };
}

// ── OSF Parser ─────────────────────────────────────────────

async function parseOSF(bytes) {
  const text = new TextDecoder('utf-8').decode(bytes);
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'application/xml');
  const lines = [];

  const paras = doc.querySelectorAll('para, paragraph');
  paras.forEach((para) => {
    const type = (para.getAttribute('type') || para.getAttribute('element') || 'action').toLowerCase();
    const content = para.textContent.trim();
    if (!content) { lines.push(''); return; }

    if (type.includes('scene') && type.includes('heading')) {
      lines.push('', content.toUpperCase(), '');
    } else if (type === 'character') {
      lines.push('', '          ' + content.toUpperCase());
    } else if (type === 'parenthetical') {
      lines.push('    (' + content + ')');
    } else if (type === 'dialogue') {
      lines.push('    ' + content);
    } else if (type === 'transition') {
      lines.push('', '                                        ' + content.toUpperCase(), '');
    } else {
      lines.push(content);
    }
  });

  if (lines.length === 0) {
    return { text: doc.documentElement.textContent, pageCount: null, metadata: {} };
  }

  return { text: lines.join('\n'), pageCount: null, metadata: {} };
}

// ── Fade In Parser ─────────────────────────────────────────

async function parseFadeIn(bytes, buffer) {
  try {
    const zip = await loadAndUnzip(buffer);
    const xmlKey = Object.keys(zip).find((k) => k === 'document.xml')
      || Object.keys(zip).find((k) => k.endsWith('.xml'));
    if (!xmlKey) throw new Error('No XML in Fade In archive');
    return parseFadeInXML(zip[xmlKey]);
  } catch (e) {
    // Might be raw XML
    const text = new TextDecoder('utf-8').decode(bytes);
    return parseFadeInXML(text);
  }
}

function parseFadeInXML(xmlText) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, 'application/xml');
  const lines = [];

  const paragraphs = doc.querySelectorAll('paragraph');
  paragraphs.forEach((para) => {
    const type = (para.getAttribute('type') || para.getAttribute('element') || 'action').toLowerCase();
    const content = para.textContent.trim();
    if (!content) { lines.push(''); return; }

    if (type.includes('scene') && type.includes('heading')) {
      lines.push('', content.toUpperCase(), '');
    } else if (type.includes('character')) {
      lines.push('', '          ' + content.toUpperCase());
    } else if (type.includes('parenthetical')) {
      lines.push('    (' + content + ')');
    } else if (type.includes('dialogue')) {
      lines.push('    ' + content);
    } else if (type.includes('transition')) {
      lines.push('', '                                        ' + content.toUpperCase(), '');
    } else {
      lines.push(content);
    }
  });

  if (lines.length === 0) {
    return { text: doc.documentElement.textContent, pageCount: null, metadata: {} };
  }

  return { text: lines.join('\n'), pageCount: null, metadata: {} };
}

// ── Highland Parser ────────────────────────────────────────

async function parseHighland(bytes, buffer) {
  try {
    const zip = await loadAndUnzip(buffer);
    const fountainKey = Object.keys(zip).find(
      (k) => k.endsWith('.fountain') || k === 'content.fountain' || k === 'content'
    ) || Object.keys(zip).find(
      (k) => k.endsWith('.txt') && !k.startsWith('__')
    );

    if (fountainKey) {
      const encoder = new TextEncoder();
      const decoded = zip[fountainKey];
      return parseFountain(encoder.encode(decoded));
    }

    throw new Error('No Fountain content in Highland package');
  } catch (e) {
    // Might be raw Fountain
    return parseFountain(bytes);
  }
}

// ── ZIP Utility (lightweight, no dependency) ───────────────

async function loadAndUnzip(buffer) {
  // Use JSZip if available, otherwise use a minimal ZIP reader
  if (window.JSZip) {
    const zip = await window.JSZip.loadAsync(buffer);
    const files = {};
    for (const [name, entry] of Object.entries(zip.files)) {
      if (!entry.dir) {
        files[name] = await entry.async('string');
      }
    }
    return files;
  }

  // Dynamically load JSZip from CDN
  if (!window._jsZipLoading) {
    window._jsZipLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load JSZip'));
      document.head.appendChild(script);
    });
  }

  await window._jsZipLoading;
  const zip = await window.JSZip.loadAsync(buffer);
  const files = {};
  for (const [name, entry] of Object.entries(zip.files)) {
    if (!entry.dir) {
      files[name] = await entry.async('string');
    }
  }
  return files;
}
